<template>
  <div class="travel-showcase-container">
    <div class="showcase-header" data-aos="fade-up">
      <h2 class="showcase-title">旅行足迹</h2>
      <p class="showcase-subtitle">用镜头记录世界的美好</p>
    </div>
    
    <div class="device-container" data-aos="zoom-in" data-aos-delay="300">
      <!-- 设备切换按钮 -->
      <div class="device-switcher">
        <button 
          :class="['device-btn', { active: currentDevice === 'tv' }]"
          @click="switchDevice('tv')"
        >
          📺 复古电视
        </button>
        <button 
          :class="['device-btn', { active: currentDevice === 'phone' }]"
          @click="switchDevice('phone')"
        >
          📱 手机相册
        </button>
      </div>
      
      <!-- 电视机模拟器 -->
      <div v-if="currentDevice === 'tv'" class="tv-simulator">
        <div class="tv-frame">
          <div class="tv-screen">
            <div class="screen-content">
              <img 
                :src="currentImage.url" 
                :alt="currentImage.location"
                class="travel-photo"
                @load="onImageLoad"
              />
              <div class="scan-lines"></div>
              <div class="location-overlay">
                <h3 class="location-name">{{ currentImage.location }}</h3>
                <p class="location-date">{{ currentImage.date }}</p>
                <p class="location-desc">{{ currentImage.description }}</p>
              </div>
            </div>
          </div>
          <div class="tv-controls">
            <div class="control-panel">
              <div class="control-knob volume-knob"></div>
              <div class="control-knob channel-knob"></div>
              <div class="control-buttons">
                <div class="control-btn power-btn" @click="togglePower"></div>
                <div class="control-btn prev-btn" @click="previousImage">◀</div>
                <div class="control-btn next-btn" @click="nextImage">▶</div>
              </div>
            </div>
          </div>
          <div class="tv-stand"></div>
        </div>
      </div>
      
      <!-- 手机模拟器 -->
      <div v-if="currentDevice === 'phone'" class="phone-simulator">
        <div class="phone-frame">
          <div class="phone-notch"></div>
          <div class="phone-screen">
            <div class="status-bar">
              <span class="time">{{ currentTime }}</span>
              <div class="status-icons">
                <span class="signal">📶</span>
                <span class="wifi">📶</span>
                <span class="battery">🔋</span>
              </div>
            </div>
            <div class="photo-viewer">
              <img 
                :src="currentImage.url" 
                :alt="currentImage.location"
                class="phone-photo"
              />
              <div class="photo-info">
                <h3 class="photo-location">📍 {{ currentImage.location }}</h3>
                <p class="photo-date">📅 {{ currentImage.date }}</p>
              </div>
              <div class="photo-controls">
                <button class="photo-btn" @click="previousImage">⬅️</button>
                <span class="photo-counter">{{ currentIndex + 1 }} / {{ travelImages.length }}</span>
                <button class="photo-btn" @click="nextImage">➡️</button>
              </div>
            </div>
          </div>
          <div class="home-indicator"></div>
        </div>
      </div>
      
      <!-- 缩略图导航 -->
      <div class="thumbnail-nav" data-aos="fade-up" data-aos-delay="600">
        <div class="thumbnail-container">
          <div 
            v-for="(image, index) in travelImages" 
            :key="index"
            :class="['thumbnail', { active: index === currentIndex }]"
            @click="setCurrentImage(index)"
          >
            <img :src="image.url" :alt="image.location" />
            <div class="thumbnail-overlay">
              <span class="thumbnail-location">{{ image.location }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TravelShowcase',
  data() {
    return {
      currentDevice: 'tv',
      currentIndex: 0,
      isPlaying: true,
      currentTime: '',
      travelImages: [
        {
          url: '/placeholder.svg?height=400&width=600',
          location: '北京故宫',
          date: '2023年春',
          description: '古老的紫禁城，承载着六百年的历史沧桑'
        },
        {
          url: '/placeholder.svg?height=400&width=600',
          location: '杭州西湖',
          date: '2023年夏',
          description: '欲把西湖比西子，淡妆浓抹总相宜'
        },
        {
          url: '/placeholder.svg?height=400&width=600',
          location: '成都宽窄巷子',
          date: '2023年秋',
          description: '慢生活的代表，品味巴蜀文化的精髓'
        },
        {
          url: '/placeholder.svg?height=400&width=600',
          location: '哈尔滨冰雪大世界',
          date: '2023年冬',
          description: '冰雪奇缘的现实版，北国风光的极致展现'
        },
        {
          url: '/placeholder.svg?height=400&width=600',
          location: '青海湖',
          date: '2024年夏',
          description: '高原明珠，天空之镜般的纯净美景'
        }
      ]
    }
  },
  computed: {
    currentImage() {
      return this.travelImages[this.currentIndex]
    }
  },
  mounted() {
    this.updateTime()
    this.startAutoPlay()
    setInterval(this.updateTime, 1000)
  },
  beforeUnmount() {
    this.stopAutoPlay()
  },
  methods: {
    switchDevice(device) {
      this.currentDevice = device
    },
    nextImage() {
      this.currentIndex = (this.currentIndex + 1) % this.travelImages.length
    },
    previousImage() {
      this.currentIndex = this.currentIndex === 0 
        ? this.travelImages.length - 1 
        : this.currentIndex - 1
    },
    setCurrentImage(index) {
      this.currentIndex = index
    },
    togglePower() {
      this.isPlaying = !this.isPlaying
      if (this.isPlaying) {
        this.startAutoPlay()
      } else {
        this.stopAutoPlay()
      }
    },
    startAutoPlay() {
      this.autoPlayTimer = setInterval(() => {
        if (this.isPlaying) {
          this.nextImage()
        }
      }, 4000)
    },
    stopAutoPlay() {
      if (this.autoPlayTimer) {
        clearInterval(this.autoPlayTimer)
      }
    },
    updateTime() {
      const now = new Date()
      this.currentTime = now.toLocaleTimeString('zh-CN', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    },
    onImageLoad() {
      // 图片加载完成后的处理
    }
  }
}
</script>

<style scoped>
.travel-showcase-container {
  padding: 3rem 0;
  background: linear-gradient(135deg, rgba(240, 147, 251, 0.05) 0%, rgba(245, 87, 108, 0.05) 100%);
  border-radius: 20px;
  margin: 2rem 0;
}

.showcase-header {
  text-align: center;
  margin-bottom: 3rem;
}

.showcase-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.showcase-subtitle {
  font-size: 1.1rem;
  color: var(--text-secondary);
  margin: 0;
}

.device-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 2rem;
}

.device-switcher {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 2rem;
}

.device-btn {
  padding: 0.8rem 1.5rem;
  border: 2px solid #f093fb;
  background: transparent;
  color: var(--text-primary);
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.device-btn:hover {
  background: #f093fb;
  color: white;
  transform: translateY(-2px);
}

.device-btn.active {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(240, 147, 251, 0.3);
}

/* 电视机模拟器样式 */
.tv-simulator {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 2rem 0;
}

.tv-frame {
  background: linear-gradient(145deg, #2c2c2c, #1a1a1a);
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.3),
    inset 0 2px 4px rgba(255, 255, 255, 0.1);
  position: relative;
}

.tv-screen {
  width: 500px;
  height: 300px;
  background: #000;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  border: 8px solid #333;
  box-shadow: inset 0 0 20px rgba(0, 0, 0, 0.8);
}

.screen-content {
  width: 100%;
  height: 100%;
  position: relative;
}

.travel-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: contrast(1.1) brightness(0.9);
}

.scan-lines {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: repeating-linear-gradient(
    0deg,
    transparent,
    transparent 2px,
    rgba(255, 255, 255, 0.03) 2px,
    rgba(255, 255, 255, 0.03) 4px
  );
  pointer-events: none;
  animation: scanlines 0.1s linear infinite;
}

@keyframes scanlines {
  0% { transform: translateY(0); }
  100% { transform: translateY(4px); }
}

.location-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  color: white;
  padding: 2rem 1.5rem 1rem;
  transform: translateY(100%);
  transition: transform 0.3s ease;
}

.tv-screen:hover .location-overlay {
  transform: translateY(0);
}

.location-name {
  font-size: 1.3rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
}

.location-date {
  font-size: 0.9rem;
  opacity: 0.8;
  margin: 0 0 0.5rem 0;
}

.location-desc {
  font-size: 0.9rem;
  line-height: 1.4;
  margin: 0;
  opacity: 0.9;
}

.tv-controls {
  margin-top: 1.5rem;
  display: flex;
  justify-content: center;
}

.control-panel {
  display: flex;
  align-items: center;
  gap: 2rem;
  background: #1a1a1a;
  padding: 1rem 2rem;
  border-radius: 10px;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.5);
}

.control-knob {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #666, #333);
  border: 2px solid #555;
  position: relative;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.control-knob:hover {
  transform: scale(1.1);
}

.control-knob::after {
  content: '';
  position: absolute;
  top: 5px;
  left: 50%;
  width: 3px;
  height: 15px;
  background: #999;
  transform: translateX(-50%);
  border-radius: 2px;
}

.control-buttons {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.control-btn {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: #333;
  border: 2px solid #555;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #999;
  font-size: 0.8rem;
  font-weight: bold;
}

.control-btn:hover {
  background: #444;
  border-color: #666;
  transform: scale(1.1);
}

.power-btn {
  background: #d32f2f;
  border-color: #b71c1c;
}

.power-btn:hover {
  background: #f44336;
}

.tv-stand {
  width: 200px;
  height: 20px;
  background: linear-gradient(145deg, #2c2c2c, #1a1a1a);
  margin: 1rem auto 0;
  border-radius: 10px;
  position: relative;
}

.tv-stand::before {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 100px;
  height: 10px;
  background: #1a1a1a;
  border-radius: 5px;
}

/* 手机模拟器样式 */
.phone-simulator {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 2rem 0;
}

.phone-frame {
  width: 300px;
  height: 600px;
  background: linear-gradient(145deg, #1a1a1a, #2c2c2c);
  border-radius: 30px;
  padding: 1rem;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.3),
    inset 0 2px 4px rgba(255, 255, 255, 0.1);
  position: relative;
}

.phone-notch {
  width: 120px;
  height: 25px;
  background: #000;
  border-radius: 0 0 15px 15px;
  margin: 0 auto 0.5rem;
  position: relative;
}

.phone-screen {
  width: 100%;
  height: calc(100% - 40px);
  background: #000;
  border-radius: 20px;
  overflow: hidden;
  position: relative;
}

.status-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  font-size: 0.8rem;
}

.status-icons {
  display: flex;
  gap: 0.5rem;
}

.photo-viewer {
  height: calc(100% - 40px);
  position: relative;
  display: flex;
  flex-direction: column;
}

.phone-photo {
  flex: 1;
  width: 100%;
  object-fit: cover;
}

.photo-info {
  position: absolute;
  bottom: 60px;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  color: white;
  padding: 2rem 1rem 1rem;
}

.photo-location {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 0.3rem 0;
}

.photo-date {
  font-size: 0.9rem;
  opacity: 0.8;
  margin: 0;
}

.photo-controls {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.9);
}

.photo-btn {
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: background 0.3s ease;
}

.photo-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.photo-counter {
  color: white;
  font-size: 0.9rem;
}

.home-indicator {
  width: 120px;
  height: 4px;
  background: #666;
  border-radius: 2px;
  margin: 0.5rem auto 0;
}

/* 缩略图导航 */
.thumbnail-nav {
  margin-top: 2rem;
}

.thumbnail-container {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  max-width: 600px;
  margin: 0 auto;
}

.thumbnail {
  width: 80px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
  border: 3px solid transparent;
}

.thumbnail:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.thumbnail.active {
  border-color: #f093fb;
  box-shadow: 0 0 15px rgba(240, 147, 251, 0.5);
}

.thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  color: white;
  padding: 0.5rem 0.3rem 0.2rem;
  transform: translateY(100%);
  transition: transform 0.3s ease;
}

.thumbnail:hover .thumbnail-overlay {
  transform: translateY(0);
}

.thumbnail-location {
  font-size: 0.7rem;
  font-weight: 500;
  text-align: center;
  display: block;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .device-container {
    padding: 0 1rem;
  }
  
  .tv-screen {
    width: 350px;
    height: 210px;
  }
  
  .phone-frame {
    width: 250px;
    height: 500px;
  }
  
  .control-panel {
    gap: 1rem;
    padding: 0.8rem 1.5rem;
  }
  
  .control-knob {
    width: 30px;
    height: 30px;
  }
  
  .control-btn {
    width: 30px;
    height: 30px;
    font-size: 0.7rem;
  }
  
  .thumbnail {
    width: 60px;
    height: 45px;
  }
  
  .thumbnail-container {
    gap: 0.5rem;
  }
}

@media (max-width: 576px) {
  .tv-screen {
    width: 280px;
    height: 168px;
  }
  
  .phone-frame {
    width: 220px;
    height: 440px;
  }
  
  .device-switcher {
    flex-direction: column;
    align-items: center;
  }
  
  .device-btn {
    width: 200px;
  }
  
  .showcase-title {
    font-size: 2rem;
  }
}

/* 夜间模式适配 */
.dark-mode .device-btn {
  border-color: #f093fb;
  color: var(--text-primary);
}

.dark-mode .device-btn:hover {
  background: #f093fb;
  color: white;
}
</style>
